import { Injectable } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CampaignAudienceSegment } from '@core/campaigns';
import { ProductInterface } from '@core/products';
import { beginOfDay, endOfDay } from '@helpers/const';
import { amountValidator, arrayMinNotEmptyValidator } from '@helpers/validators';
import { budgetValidator } from '@helpers/validators/budget-validator';
import { RetailMediaCampaignType } from '@rmn/models/enums/retail-media-campaign-type.enum';
import { PartnerInterface } from '@rmn/models/retail-media-partner.interface';

// Temporary - we're waiting for business decision.
const RETAIL_MEDIA_INVENTORY_MIN_CPM = 16;
const RETAIL_MEDIA_INVENTORY_MAX_CPM = 100;
const RETAIL_MEDIA_INVENTORY_MIN_BUDGET = 1000;
const RETAIL_MEDIA_INVENTORY_MAX_BUDGET = 50_000;
const CROSS_RETAIL_EMISSION_MIN_CPM = 9;
const CROSS_RETAIL_EMISSION_MAX_CPM = 100;
const CROSS_RETAIL_EMISSION_MIN_BUDGET = 1000;
const SELF_ADVERTISING_MIN_IMPRESSIONS = 1000;

interface RetailMediaCampaignFormInterface {
    name: FormControl<string>;
    cpm?: FormControl<number | null>;
    budget?: FormControl<number | null>;
    impressions?: FormControl<number | null>;
    revenue: FormControl<number | null>;
    products: FormControl<ProductInterface[]>;
    agreement?: FormControl<boolean>;
    customerServiceAgreement?: FormControl<boolean>;
    audienceSegments?: FormGroup<{
        includes: FormControl<CampaignAudienceSegment[]>;
        excludes: FormControl<CampaignAudienceSegment[]>;
    }>;
    websites?: FormGroup<{
        included: FormControl<PartnerInterface[]>;
        excluded: FormControl<PartnerInterface[]>;
    }>;
    dateTo: FormControl<Date>;
    dateFrom: FormControl<Date>;
    campaignId: FormControl<number | null>;
    campaignType: FormControl<RetailMediaCampaignType | null>;
}

export type RetailMediaCampaignFormType = FormGroup<RetailMediaCampaignFormInterface>;

export const getMinBudgetValue = (form: RetailMediaCampaignFormType): number => {
    const type = form.controls.campaignType.value;
    const revenue = form.controls.revenue.value ?? 0;

    if (type === RetailMediaCampaignType.CROSS_RETAIL_EMISSION) {
        return Math.max(revenue, CROSS_RETAIL_EMISSION_MIN_BUDGET);
    }

    return Math.max(revenue, RETAIL_MEDIA_INVENTORY_MIN_BUDGET);
};

@Injectable({
    providedIn: 'any'
})
export class RetailMediaCampaignFormFactory {
    private audienceSegments = new FormGroup({
        includes: new FormControl<CampaignAudienceSegment[]>([], { nonNullable: true }),
        excludes: new FormControl<CampaignAudienceSegment[]>([], { nonNullable: true })
    });

    private websites = new FormGroup({
        included: new FormControl<PartnerInterface[]>([], { nonNullable: true }),
        excluded: new FormControl<PartnerInterface[]>([], { nonNullable: true })
    });

    private cpm = new FormControl<number>(RETAIL_MEDIA_INVENTORY_MIN_CPM, {
        nonNullable: false,
        validators: [amountValidator(RETAIL_MEDIA_INVENTORY_MIN_CPM, RETAIL_MEDIA_INVENTORY_MAX_CPM)]
    });

    private budget = new FormControl<number>(RETAIL_MEDIA_INVENTORY_MIN_BUDGET, {
        nonNullable: false,
        validators: [amountValidator(RETAIL_MEDIA_INVENTORY_MIN_BUDGET, RETAIL_MEDIA_INVENTORY_MAX_BUDGET)]
    });

    private impressions = new FormControl<number>(SELF_ADVERTISING_MIN_IMPRESSIONS, {
        nonNullable: false,
        validators: [Validators.min(SELF_ADVERTISING_MIN_IMPRESSIONS)]
    });

    public form: RetailMediaCampaignFormType = new FormGroup<RetailMediaCampaignFormInterface>({
        name: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        cpm: this.cpm,
        budget: this.budget,
        impressions: this.impressions,
        revenue: new FormControl<number | null>(null),
        products: new FormControl<ProductInterface[]>([], {
            nonNullable: true,
            validators: [arrayMinNotEmptyValidator()]
        }),
        agreement: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
        customerServiceAgreement: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
        dateTo: new FormControl<Date>(endOfDay(new Date()), { nonNullable: true }),
        dateFrom: new FormControl<Date>(beginOfDay(new Date()), { nonNullable: true }),
        campaignId: new FormControl(),
        campaignType: new FormControl<RetailMediaCampaignType | null>(null, {
            nonNullable: true,
            validators: [Validators.required]
        }),
        websites: this.websites,
        audienceSegments: this.audienceSegments
    });

    constructor() {
        this.form.controls.revenue.valueChanges.subscribe(() => {
            this.form.controls.budget?.clearValidators();
            this.form.controls.budget?.addValidators(this.getBudgetValidator());
        });

        this.form.controls.campaignType.valueChanges.subscribe(type => {
            if (type) {
                this.rebuildParametersForm(type);
            }
        });

        this.rebuildParametersForm(RetailMediaCampaignType.RETAIL_MEDIA_INVENTORY);
    }

    private rebuildParametersForm(type: RetailMediaCampaignType): void {
        switch (type) {
            case RetailMediaCampaignType.CROSS_RETAIL_EMISSION:
                this.form.removeControl('impressions');
                this.form.addControl('audienceSegments', this.audienceSegments);
                this.form.addControl('websites', this.websites);

                this.form.addControl('cpm', this.cpm);
                this.form.controls.cpm?.clearValidators();
                this.form.controls.cpm?.addValidators([amountValidator(CROSS_RETAIL_EMISSION_MIN_CPM, CROSS_RETAIL_EMISSION_MAX_CPM)]);

                this.form.addControl('budget', this.budget);
                this.form.controls.budget?.clearValidators();
                this.form.controls.budget?.addValidators(this.getBudgetValidator());

                if (!this.form.controls.cpm?.value || this.form.controls.cpm.value < CROSS_RETAIL_EMISSION_MIN_CPM) {
                    this.form.controls.cpm?.setValue(CROSS_RETAIL_EMISSION_MIN_CPM);
                }

                if (!this.form.controls.budget?.value || this.form.controls.budget.value < CROSS_RETAIL_EMISSION_MIN_BUDGET) {
                    this.form.controls.budget?.setValue(CROSS_RETAIL_EMISSION_MIN_BUDGET);
                }

                break;
            case RetailMediaCampaignType.RETAIL_MEDIA_INVENTORY:
                this.form.removeControl('impressions');
                this.form.removeControl('audienceSegments');
                this.form.addControl('websites', this.websites);

                this.form.addControl('cpm', this.cpm);
                this.form.controls.cpm?.clearValidators();
                this.form.controls.cpm?.addValidators([amountValidator(RETAIL_MEDIA_INVENTORY_MIN_CPM)]);

                this.form.addControl('budget', this.budget);
                this.form.controls.budget?.clearValidators();
                this.form.controls.budget?.addValidators(this.getBudgetValidator());

                if (!this.form.controls.cpm?.value || this.form.controls.cpm.value < RETAIL_MEDIA_INVENTORY_MIN_CPM) {
                    this.form.controls.cpm?.setValue(RETAIL_MEDIA_INVENTORY_MIN_CPM);
                }

                if (!this.form.controls.budget?.value || this.form.controls.budget.value < RETAIL_MEDIA_INVENTORY_MIN_BUDGET) {
                    this.form.controls.budget?.setValue(RETAIL_MEDIA_INVENTORY_MIN_BUDGET);
                }

                break;
            case RetailMediaCampaignType.RETAIL_MEDIA_SELF_ADVERTISING:
                this.form.addControl('impressions', this.impressions);
                this.form.removeControl('audienceSegments');
                this.form.removeControl('websites');
                this.form.removeControl('cpm');
                this.form.removeControl('budget');
        }
    }

    private getBudgetValidator(): ValidatorFn {
        const type = this.form.controls.campaignType.value;
        const revenue = this.form.controls.revenue.value ?? 0;

        if (type === RetailMediaCampaignType.CROSS_RETAIL_EMISSION) {
            if (revenue > CROSS_RETAIL_EMISSION_MIN_BUDGET) {
                return budgetValidator(revenue);
            }

            return amountValidator(CROSS_RETAIL_EMISSION_MIN_BUDGET);
        }

        if (revenue > RETAIL_MEDIA_INVENTORY_MIN_BUDGET) {
            return budgetValidator(revenue, RETAIL_MEDIA_INVENTORY_MAX_BUDGET);
        }

        return amountValidator(RETAIL_MEDIA_INVENTORY_MIN_BUDGET, RETAIL_MEDIA_INVENTORY_MAX_BUDGET);
    }
}
